import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { isSupabaseConfigured, supabase } from '../lib/supabase';

type AuthMode = 'login' | 'signup';

type AuthModalProps = {
  isOpen: boolean;
  initialMode?: AuthMode;
  onClose: () => void;
};

const AuthModal = ({ isOpen, initialMode = 'login', onClose }: AuthModalProps) => {
  const [mode, setMode] = React.useState<AuthMode>(initialMode);
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [fullName, setFullName] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [error, setError] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (isOpen) {
      setMode(initialMode);
      setMessage('');
      setError('');
    }
  }, [initialMode, isOpen]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setMessage('');
    setError('');

    if (!isSupabaseConfigured) {
      setError('Account access is temporarily unavailable.');
      return;
    }

    setIsSubmitting(true);

    try {
      if (mode === 'signup') {
        const { error: signUpError } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/client`,
            data: { full_name: fullName },
          },
        });

        if (signUpError) throw signUpError;
        setMessage('Account created. Check your email if confirmation is required, then log in.');
      } else {
        const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
        if (signInError) throw signInError;
        setMessage('Logged in. You can continue the consult now.');
        setTimeout(onClose, 450);
      }
    } catch (authError: any) {
      setError(authError.message || 'Account access failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[300] flex items-center justify-center bg-black/75 px-4 backdrop-blur-xl"
          onMouseDown={onClose}
        >
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96, filter: 'blur(10px)' }}
            animate={{ opacity: 1, y: 0, scale: 1, filter: 'blur(0px)' }}
            exit={{ opacity: 0, y: 18, scale: 0.98, filter: 'blur(8px)' }}
            transition={{ duration: 0.22, ease: 'easeOut' }}
            className="relative w-full max-w-md overflow-hidden rounded-[2rem] border border-orange-300/25 bg-[#060505] p-6 text-orange-50 shadow-[0_0_70px_rgba(249,115,22,0.22)]"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="pointer-events-none absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-orange-200/80 to-transparent" />
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_80%_0%,rgba(249,115,22,0.22),transparent_36%)]" />
            <div className="relative z-10">
              <div className="flex items-start justify-between gap-5">
                <div>
                  <p className="text-xs font-black uppercase tracking-[0.24em] text-orange-300">Client access</p>
                  <h2 className="mt-2 text-3xl font-black tracking-tight">
                    {mode === 'login' ? 'Log in to continue.' : 'Create your account.'}
                  </h2>
                  <p className="mt-3 text-sm leading-6 text-orange-100/58">
                    Save the brief, continue the consult, and keep the checkout tied to one secure client profile.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-full border border-white/10 bg-white/5 px-3 py-2 text-xs font-black uppercase tracking-wider text-orange-100/70 transition-colors hover:border-orange-300/60 hover:text-orange-50"
                >
                  Close
                </button>
              </div>

              <div className="mt-6 grid grid-cols-2 rounded-full border border-white/10 bg-white/[0.04] p-1">
                {(['login', 'signup'] as AuthMode[]).map((nextMode) => (
                  <button
                    key={nextMode}
                    type="button"
                    onClick={() => {
                      setMode(nextMode);
                      setMessage('');
                      setError('');
                    }}
                    className={`rounded-full px-4 py-2 text-sm font-black capitalize transition ${
                      mode === nextMode ? 'bg-orange-400 text-black' : 'text-orange-100/65 hover:text-orange-50'
                    }`}
                  >
                    {nextMode}
                  </button>
                ))}
              </div>

              <form className="mt-6 space-y-3" onSubmit={handleSubmit}>
                {mode === 'signup' && (
                  <input
                    value={fullName}
                    onChange={(event) => setFullName(event.target.value)}
                    placeholder="Full name"
                    className="w-full rounded-2xl border border-white/10 bg-black/35 px-4 py-3 text-sm text-orange-50 outline-none transition-colors placeholder:text-orange-100/35 focus:border-orange-300/70"
                  />
                )}
                <input
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  type="email"
                  placeholder="Email address"
                  className="w-full rounded-2xl border border-white/10 bg-black/35 px-4 py-3 text-sm text-orange-50 outline-none transition-colors placeholder:text-orange-100/35 focus:border-orange-300/70"
                  required
                />
                <input
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  type="password"
                  placeholder="Password"
                  className="w-full rounded-2xl border border-white/10 bg-black/35 px-4 py-3 text-sm text-orange-50 outline-none transition-colors placeholder:text-orange-100/35 focus:border-orange-300/70"
                  required
                />
                {error && <p className="rounded-2xl border border-red-400/30 bg-red-500/10 p-3 text-sm text-red-100">{error}</p>}
                {message && (
                  <p className="rounded-2xl border border-orange-300/25 bg-orange-500/10 p-3 text-sm text-orange-100">
                    {message}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full rounded-full bg-gradient-to-r from-orange-500 to-amber-400 px-5 py-3 font-black text-black transition-colors hover:from-orange-400 hover:to-amber-300 disabled:cursor-wait disabled:opacity-70"
                >
                  {isSubmitting ? 'Working...' : mode === 'login' ? 'Log in' : 'Create account'}
                </button>
              </form>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AuthModal;
